import { useState, useEffect, useCallback } from 'react';
import { checkUserHasConsentedToPersonalisation } from '@zoopla/consent-utils';

export type SearchEntry = {
    id: string;
    text: string;
    subValue?: string;
    type?: string;
};

const useSearchHistory = ({ searchType }: { searchType?: string }) => {
    const [searchHistory, setSearchHistory] = useState<SearchEntry[]>([]);

    const getLocalStorageKey = useCallback(
        () =>
            `search-history-${
                searchType === 'house-prices' ? 'house-prices' : 'property'
            }`,
        [searchType]
    );

    const updateHistory = useCallback(() => {
        if (typeof window !== 'undefined') {
            const savedSearches = localStorage.getItem(getLocalStorageKey());

            if (savedSearches) {
                setSearchHistory(JSON.parse(savedSearches));
            }
        }
    }, [getLocalStorageKey]);

    useEffect(() => {
        updateHistory();

        const onHistoryChange = () => {
            updateHistory();
        };

        window.addEventListener('searchHistoryChange', onHistoryChange);
        return () =>
            window.removeEventListener('searchHistoryChange', onHistoryChange);
    }, [searchType, updateHistory]);

    const addSearchToHistory = (newSearch: SearchEntry): void => {
        if (
            typeof window !== 'undefined' &&
            searchType &&
            checkUserHasConsentedToPersonalisation()
        ) {
            const currentSearchesString = localStorage.getItem(
                getLocalStorageKey()
            );
            const currentSearches: SearchEntry[] = currentSearchesString
                ? JSON.parse(currentSearchesString)
                : [];
            const updatedSearches = [
                { ...newSearch, type: 'search-history' },
                ...currentSearches.filter(
                    (search) => search.text !== newSearch.text
                )
            ].slice(0, 12);
            localStorage.setItem(
                getLocalStorageKey(),
                JSON.stringify(updatedSearches)
            );
            setSearchHistory(updatedSearches);

            // Dispatch custom event
            window.dispatchEvent(new CustomEvent('searchHistoryChange'));
        }
    };

    const deleteSearchFromHistory = (textToDelete: string): void => {
        setSearchHistory((prevSearches) => {
            const updatedSearches = prevSearches.filter(
                (search) => search.text !== textToDelete
            );
            if (typeof window !== 'undefined' && searchType) {
                localStorage.setItem(
                    getLocalStorageKey(),
                    JSON.stringify(updatedSearches)
                );
            }
            // Dispatch custom event
            window.dispatchEvent(new CustomEvent('searchHistoryChange'));
            return updatedSearches;
        });
    };

    const clearSearchHistory = (): void => {
        setSearchHistory([]);
        if (typeof window !== 'undefined' && searchType) {
            localStorage.removeItem(getLocalStorageKey());

            // Dispatch custom event
            window.dispatchEvent(new CustomEvent('searchHistoryChange'));
        }
    };

    return {
        searchHistory,
        addSearchToHistory,
        deleteSearchFromHistory,
        clearSearchHistory
    };
};

export { useSearchHistory };
